import { forwardRef } from "react";
import {
  EXAM_SECTIONS,
  PARACLINICAL_SECTIONS,
  RAPID_TESTS_DUAL,
  RAPID_TESTS_TEXT,
} from "@/lib/consultationSchema";

/**
 * Print-only rendering of a consultation. Hidden on screen (display:none)
 * and shown by print.css @media print rules so `window.print()` produces a
 * clean sheet without any of the app chrome.
 *
 * The consultation state comes straight from the Consultation page so this
 * matches what the vet has entered — no round trip to the server needed.
 */
const PrintableConsultation = forwardRef(function PrintableConsultation(
  { consultation, patient, diagnoses, lang, t, tField, tSection, tGroup },
  ref
) {
  const c = consultation || {};
  const p = patient || {};
  const owner = p.owner || {};
  const ext = c.extended_sections || {};
  const enabledExam = ext.enabledExam || [];
  const enabledParaclinical = ext.enabledParaclinical || [];

  return (
    <div ref={ref} className="pf-print-sheet">
      <header className="pf-print-header">
        {/* Paw-only Pawfile logo (matches the PDF and TopNav) */}
        <svg
          viewBox="0 0 256 256"
          xmlns="http://www.w3.org/2000/svg"
          className="pf-print-logo"
          aria-hidden="true"
        >
          <rect width="256" height="256" rx="46" ry="46" fill="#2B7A78" />
          <path
            fill="#FFFFFF"
            d="M240,108a28,28,0,1,1-28-28A28,28,0,0,1,240,108ZM72,108a28,28,0,1,0-28,28A28,28,0,0,0,72,108ZM92,88A28,28,0,1,0,64,60,28,28,0,0,0,92,88Zm72,0a28,28,0,1,0-28-28A28,28,0,0,0,164,88Zm23.12,60.86a35.3,35.3,0,0,1-16.87-21.14,44,44,0,0,0-84.5,0A35.25,35.25,0,0,1,69,148.82,40,40,0,0,0,88,224a39.48,39.48,0,0,0,15.52-3.13,64.09,64.09,0,0,1,48.87,0,40,40,0,0,0,34.73-72Z"
          />
        </svg>
      </header>

      <h1 className="pf-print-title">{t("consultationReport")}</h1>

      <div className="pf-print-two-col">
        <div className="pf-print-card">
          <div className="pf-print-label">{t("patient")?.toUpperCase() || "PATIENT"}</div>
          <div className="pf-print-strong">{p.name || "—"}</div>
          <div>{[p.species, p.breed].filter(Boolean).join(" • ") || "—"}</div>
          <div>
            {t("sex") || "Sex"}: {p.sex === "m" ? "Mascul" : p.sex === "f" ? "Femelă" : "—"}
          </div>
        </div>
        <div className="pf-print-card">
          <div className="pf-print-label">{t("owner")?.toUpperCase() || "OWNER"}</div>
          <div className="pf-print-strong">{owner.full_name || "—"}</div>
          {owner.phone && <div>{owner.phone}</div>}
          {owner.email && <div>{owner.email}</div>}
          {owner.address && <div>{owner.address}</div>}
        </div>
      </div>

      {c.anamnesis && (
        <PrintSection title={t("anamnesis") || "Anamneză"} text={c.anamnesis} />
      )}
      {c.clinical_examination && (
        <PrintSection title={t("clinicalExamination") || "Examen clinic"} text={c.clinical_examination} />
      )}

      {/* Diagnoses (structured + free text) */}
      {(diagnoses?.length > 0 || (c.diagnosis || "").trim()) && (
        <div className="pf-print-block">
          <h2>{t("diagnosis") || "Diagnostic"}</h2>
          {diagnoses?.length > 0 && (
            <div className="pf-print-strong">{diagnoses.map((d) => d.name).join(", ")}</div>
          )}
          {(c.diagnosis || "").trim() && (
            <div className="pf-print-text">{c.diagnosis}</div>
          )}
        </div>
      )}

      {c.treatment && <PrintSection title={t("treatment") || "Tratament"} text={c.treatment} />}
      {c.recommendations && (
        <PrintSection title={t("recommendations") || "Recomandări"} text={c.recommendations} />
      )}
      {c.observations && (
        <PrintSection title={t("observations") || "Observații"} text={c.observations} />
      )}

      {/* Sumar (Examen clinic free-text summary) */}
      {enabledExam.includes("sumar") && (ext.sumar || "").trim() && (
        <PrintSection title={lang === "ro" ? "Sumar" : "Summary"} text={ext.sumar} />
      )}

      {/* Examen clinic subsections */}
      {enabledExam.filter((id) => id !== "sumar").length > 0 && (
        <>
          <h2 className="pf-print-tab-h">{lang === "ro" ? "Examen clinic" : "Clinical exam"}</h2>
          {EXAM_SECTIONS.filter((s) => s.id !== "sumar" && enabledExam.includes(s.id)).map(
            (section) => (
              <ExamSectionPrint
                key={section.id}
                section={section}
                data={(ext.exam || {})[section.id] || {}}
                tSection={tSection}
                tGroup={tGroup}
                tField={tField}
              />
            )
          )}
        </>
      )}

      {/* Investigații paraclinice */}
      {enabledParaclinical.length > 0 && (
        <>
          <h2 className="pf-print-tab-h">
            {lang === "ro" ? "Investigații paraclinice" : "Paraclinical investigations"}
          </h2>
          {PARACLINICAL_SECTIONS.filter((s) => enabledParaclinical.includes(s.id)).map((section) => (
            <ParaclinicalSectionPrint
              key={section.id}
              section={section}
              data={(ext.paraclinical || {})[section.id] || {}}
              tSection={tSection}
              tGroup={tGroup}
              tField={tField}
            />
          ))}
        </>
      )}
    </div>
  );
});

function PrintSection({ title, text }) {
  if (!text || !String(text).trim()) return null;
  const isHtml = /<[a-z][\s\S]*>/i.test(String(text));
  return (
    <div className="pf-print-block">
      <h2>{title}</h2>
      {isHtml ? (
        <div className="pf-print-text pf-print-html" dangerouslySetInnerHTML={{ __html: text }} />
      ) : (
        <div className="pf-print-text">{text}</div>
      )}
    </div>
  );
}

function ExamSectionPrint({ section, data, tSection, tGroup, tField }) {
  // Any non-empty value in this section becomes a printable line.
  const groups = section.groups || [];
  const lines = [];
  groups.forEach((g) => {
    (g.fields || []).forEach((f) => {
      const v = data[f.key];
      if (typeof v === "string" && v.trim()) {
        lines.push({ label: tField(f.labelKey), value: v, groupTitle: g.titleKey ? tGroup(g.titleKey) : "" });
      }
    });
  });
  if (lines.length === 0) return null;
  return (
    <div className="pf-print-block">
      <h3>{tSection(section.titleKey)}</h3>
      {lines.map((l, i) => (
        <div key={i} className="pf-print-line">
          <b>{l.label}:</b> {l.value}
        </div>
      ))}
    </div>
  );
}

function ParaclinicalSectionPrint({ section, data, tSection, tGroup, tField }) {
  // Lab table (biochemistry / hematology / etc.)
  if (section.type === "lab" || section.type === "lab_with_obs") {
    const rows = (data.rows || []).filter((r) => String(r.result ?? "").trim() !== "");
    const obsKey = section.obsKey || "observatii";
    const obsVal = section.type === "lab_with_obs" ? (data[obsKey] || "").trim() : "";
    if (rows.length === 0 && !obsVal) {
      if (section.id === "biochimie") {
        // English fallback per spec — always in English even in RO PDFs.
        return (
          <div className="pf-print-block">
            <h3>{tSection(section.titleKey)}</h3>
            <div className="pf-print-text">no biochemistry rows</div>
          </div>
        );
      }
      return null;
    }
    return (
      <div className="pf-print-block">
        <h3>{tSection(section.titleKey)}</h3>
        {rows.length > 0 && (
          section.id === "sediment_urinar" ? (
            <table className="pf-print-table">
              <thead>
                <tr>
                  <th>Parametru</th>
                  <th>Rezultat</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.parameter}</td>
                    <td>{r.result}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="pf-print-table">
              <thead>
                <tr>
                  <th>Parametru</th>
                  <th>Rezultat</th>
                  <th>Unitate</th>
                  <th>Interval</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{r.parameter}</td>
                    <td>{r.result}</td>
                    <td>{r.unit || ""}</td>
                    <td>
                      {(r.ref_min ?? "—")} – {(r.ref_max ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
        {obsVal && (
          <div className="pf-print-line">
            <b>{tField(section.obsLabelKey || "fObservatii")}:</b> {obsVal}
          </div>
        )}
      </div>
    );
  }

  // Rapid tests
  if (section.type === "rapid_tests") {
    const dual = RAPID_TESTS_DUAL.map((name) => {
      const v = data[name] || {};
      const parts = [];
      if (v.result) parts.push(v.result);
      if (v.obsEnabled && (v.obs || "").trim()) parts.push(v.obs);
      return parts.length ? { name, text: parts.join(" — ") } : null;
    }).filter(Boolean);
    const textT = RAPID_TESTS_TEXT.map((name) => {
      const v = (data[name] || "").trim();
      return v ? { name, text: v } : null;
    }).filter(Boolean);
    if (dual.length === 0 && textT.length === 0) return null;
    return (
      <div className="pf-print-block">
        <h3>{tSection(section.titleKey)}</h3>
        {[...dual, ...textT].map((row, i) => (
          <div key={i} className="pf-print-line">
            <b>{row.name}:</b> {row.text}
          </div>
        ))}
      </div>
    );
  }

  // Generic groups
  const groups = section.groups || [];
  const lines = [];
  groups.forEach((g) => {
    (g.fields || []).forEach((f) => {
      const v = data[f.key];
      if (typeof v === "string" && v.trim()) {
        lines.push({ label: tField(f.labelKey), value: v });
      }
    });
  });
  if (lines.length === 0) return null;
  return (
    <div className="pf-print-block">
      <h3>{tSection(section.titleKey)}</h3>
      {lines.map((l, i) => (
        <div key={i} className="pf-print-line">
          <b>{l.label}:</b> {l.value}
        </div>
      ))}
    </div>
  );
}

export default PrintableConsultation;
